import { useEffect, useState } from "react";
import { Text, TouchableOpacity } from "react-native";
import { RootState } from "@/_store/store";
import { ILoginResponse, IUsersInfo } from "@/_store/auth/contracts";
import { useSelector } from "react-redux";
import styled from "styled-components/native";
import { usePathname, useRouter } from "expo-router";
import EyeVisibleIcon from "@/assets/icons/auth/eyeVisibleIcon.svg";
import EyeInvisibleIcon from "@/assets/icons/auth/eyeInvisibleIcon.svg";
import { validateLogin } from "@/_utils/validateAuth";
import { useAppDispatch } from "@/_hooks";
import { useLoginMutation } from "@/_store/auth/auth.slice";
import { clearAuthError, setAuthError, setAuthToken } from "@/_reducers/accounts.reducer";
import CustomButton from "@/app/components/CustomButton";
import ChooseAccount from "@/app/(auth)/choose-account";
import Container from "@/app/components/Container";
import useFocus from "@/_hooks/useFocus";
import { usePlatformStyles } from "@/_hooks/usePlatformStyles";
import { useLazyGetCurrentUserQuery } from "@/_store/currentUser/currentUser.slice";

const Login = () => {
  const router = useRouter();
  const pathname = usePathname();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [selectedAccount, setSelectedAccount] = useState<boolean>(false);
  const [availableAccounts, setAvailableAccounts] = useState<IUsersInfo[] | null>(null);
  const dispatch = useAppDispatch();

  const [signIn, { isLoading }] = useLoginMutation();
  const [fetchUser] = useLazyGetCurrentUserQuery();

  const authToken: string | null = useSelector((state: RootState) => state.account?.authToken);

  const isButtonDisabled: boolean = !validateLogin({ email, password }) || isLoading;
  const emailFocus = useFocus();
  const passwordFocus = useFocus();
  const { webOutlineNone } = usePlatformStyles();

  useEffect(() => {
    if (!authToken) return;

    if (pathname !== "/login") {
      router.replace("/general");
    }
  }, [authToken, pathname, router]);

  const handleSubmit = async (): Promise<void> => {
    try {
      const data: ILoginResponse = await signIn({ email, password }).unwrap();
      const { users, count } = data ?? {};


      if (count > 1) {
        setAvailableAccounts(users);
        setSelectedAccount(true);
        return;
      }

      const token: string = users[0].token;
      dispatch(setAuthToken(token));
      dispatch(clearAuthError());

      const userResponse = await fetchUser().unwrap();

      if (!userResponse.accepted_personal_data) {
        router.replace("/user-agreement");

        return;
      }

      router.replace("/general");

    } catch (err: any) {
      console.error(err);
      dispatch(setAuthError(err))
    }
  };

  if (selectedAccount && availableAccounts) return <ChooseAccount accounts={availableAccounts}/>;

  return (
    <Container>
      <Text className="text-header">Авторизация</Text>
      <Text className="text-subhead">Пожалуйста, введите ваш адрес электронной почты и пароль.</Text>
      <Form>
        <Label>Email</Label>
        <Input
          placeholder="Введите ваш email"
          placeholderTextColor="#9FABBE"
          value={email}
          onChangeText={setEmail}
          autoCapitalize="none"
          keyboardType="email-address"
          underlineColorAndroid="transparent"
          onFocus={emailFocus.onFocus}
          isFocused={emailFocus.isFocused}
          onBlur={emailFocus.onBlur}
          style={webOutlineNone}
        />

        <Label>Пароль</Label>
        <PasswordContainer isFocused={passwordFocus.isFocused}>
          <PasswordInput
            placeholder="Введите пароль"
            placeholderTextColor="#9FABBE"
            value={password}
            onChangeText={setPassword}
            secureTextEntry={!showPassword}
            style={webOutlineNone}
            onFocus={passwordFocus.onFocus}
            isFocused={passwordFocus.isFocused}
            onBlur={passwordFocus.onBlur}
          />
          <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
            {showPassword ? (
              <EyeVisibleIcon width={24} height={24} />
            ) : (
              <EyeInvisibleIcon width={24} height={24} />
            )}
          </TouchableOpacity>
        </PasswordContainer>

        <CustomButton
          className="mb-6"
          title="Войти"
          type="primary"
          onPress={handleSubmit}
          disabled={isButtonDisabled}
        />
      </Form>

      <CustomButton
        title="Забыли пароль?"
        type="text"
        onPress={() => router.push("/reset-password")}
        textClassName="text-blue-500"
      />
    </Container>
  );
};

export default Login;

export const Form = styled.View`
  width: 100%;
  max-width: 440px;
  align-self: flex-start;
`;

export const Label = styled.Text`
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 8px;
`;

export const Input = styled.TextInput.attrs({
  underlineColorAndroid: "transparent",
})<{ isFocused?: boolean }>`
    border: 1px solid ${(props) => props.isFocused ? "#1677FF" : "#C5CDD8"};
    border-radius: 16px;
    padding: 12px 16px;
    font-size: 16px;
    margin-bottom: 24px;
    color: #101928;
    background-color: #fff;
    elevation: 0;
`;

export const PasswordContainer = styled.View<{ isFocused?: boolean}>`
  flex-direction: row;
  align-items: center;
  border: 1px solid ${(props) => props.isFocused ? "#1677FF" : "#C5CDD8" };
  border-radius: 16px;
  padding-right: 20px;
  margin-bottom: 24px;
`;

export const PasswordInput = styled.TextInput<{ isFocused?: boolean }>`
  flex: 1;
  padding: 12px 16px;
  font-size: 16px;
`;
